/**
 * RunChip.tsx — PLAT-11690
 *
 * The presentational shell behind every "something is running here" chip beside
 * the language toggle: `DubRunChip` (PLAT-11570) and `TranslationRunChip`.
 * Extracted when translation got a run of its own, so the two cannot drift —
 * the width discipline below is the whole reason this component exists, and a
 * second hand-rolled copy would lose it the first time someone added a label.
 *
 * **It is as narrow as it is because nothing here can shrink.** The chip lands
 * in the transcript toolbar's `HeaderTail`, which is `flex-shrink: 0` inside a
 * `flex-shrink: 0` row — so a chip wider than the room left over does not
 * ellipsize, it overruns the pane and is CUT by the panel's edge, taking the
 * percent and the Cancel with it. In the review window's narrow transcript pane
 * there is ~145px after the language toggle, and "Dubbing uk-UA… 43%" ✕ needed
 * more, which is why the percent was invisible. So the label is the two things
 * a reader cannot infer — the number, then the code — and the verb (which the
 * spinner beside it already implies) lives in the hover.
 *
 * A dub and a translation can run at once, so two of these can share that
 * ~145px; `RunChipRow` is what they sit in, and `RunCode` is what gives way.
 */
import { COMPACT_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import {
  InProgressChip,
  InProgressSpinner,
  InProgressText,
} from '@src/components/transcript/inProgressChipStyles';
import CloseIcon from '@src/svgs/CloseIcon';
import { useId } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

export interface RunChipProps {
  /** 0-100, already rounded by the caller. */
  percent: number;
  /** The canonical BCP-47 code — `pt-BR` and `pt-PT` are not interchangeable. */
  code: string;
  /** The whole sentence, for a reader who cannot see the spinner. */
  ariaLabel: string;
  /** The same sentence on hover, where the chip itself has no room for it. */
  tooltip: string;
  cancelLabel: string;
  cancelTooltip: string;
  onCancel: () => void;
}

export function RunChip({
  percent,
  code,
  ariaLabel,
  tooltip,
  cancelLabel,
  cancelTooltip,
  onCancel,
}: RunChipProps) {
  // Per instance, not a module constant: Content Studio keeps every tab mounted
  // (`ContentView`'s KeepMountedWrapper) and the clip preview opens over them,
  // so several chips coexist — and this tooltip portals to <body>, escaping the
  // hidden tabs' `display: none`. A shared id would make react-tooltip open
  // every one of them from one hover.
  const tooltipId = useId();
  return (
    <>
      <InProgressChip role="status" aria-live="polite" aria-label={ariaLabel}>
        <InProgressSpinner />
        {/* The percent FIRST, because the panel cuts from the right: whatever
            is leftmost is what survives a pane too narrow for the whole chip.
            The code follows it — the caption would not fit, and the region has
            to stay. */}
        <RunLabel data-tooltip-id={tooltipId} data-tooltip-content={tooltip}>
          <RunPercent>{percent}%</RunPercent>
          <RunCode>{code}</RunCode>
        </RunLabel>
        <CancelButton
          type="button"
          aria-label={cancelLabel}
          data-tooltip-id={tooltipId}
          data-tooltip-content={cancelTooltip}
          onClick={onCancel}
        >
          <CloseIcon width={10} height={10} />
        </CancelButton>
      </InProgressChip>
      {createPortal(
        <Tooltip
          id={tooltipId}
          style={COMPACT_TOOLTIP_STYLE}
          place="bottom"
          positionStrategy="fixed"
        />,
        document.body,
      )}
    </>
  );
}

/**
 * Holds however many chips are running. `min-width: 0` is the point: without it
 * a flex item refuses to shrink below its content, so two chips in the narrow
 * review pane would push past the edge and be cut instead of letting their
 * codes ellipsize.
 */
export const RunChipRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

const RunLabel = styled(InProgressText)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

/**
 * Never truncated and never wrapped: the number is the whole point of the chip,
 * so it is the one part that keeps its width whatever the pane leaves.
 */
const RunPercent = styled.span`
  flex: none;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

/**
 * The part that gives way first. Capped and clipped rather than left to size
 * itself, so that no future label can push the toolbar off its own edge again —
 * which is exactly what the full caption did.
 */
const RunCode = styled.span`
  min-width: 0;
  max-width: 72px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CancelButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: var(--radius-button);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;

  &:hover {
    background-color: var(--surface-elevated);
    color: var(--foreground);
  }
`;
